import { format } from "date-fns";
import { X, Package, User, Coins, Calendar, FileText } from "lucide-react";
import type { MarketplaceOrder, MarketplaceCategory, OrderStatus } from "../types";
import { CATEGORY_EMOJI, CATEGORY_LABEL_KEYS, koinsToEur } from "../constants";
import { useLocale } from "../../../context/LanguageContext";
import { dateFnsLocale } from "../../../i18n/date";

const STATUS_STYLES: Record<OrderStatus, string> = {
  PENDING: "bg-warning/10 text-warning",
  PROCESSING: "bg-secondary/10 text-secondary",
  FULFILLED: "bg-success/10 text-success",
  CANCELLED: "bg-destructive/10 text-destructive",
  REFUNDED: "bg-muted text-muted-foreground",
};

const STATUS_LABEL_KEYS: Record<OrderStatus, string> = {
  PENDING: "orders.statusPending",
  PROCESSING: "orders.statusProcessing",
  FULFILLED: "orders.statusFulfilled",
  CANCELLED: "orders.statusCancelled",
  REFUNDED: "orders.statusRefunded",
};

interface OrderDetailSheetProps {
  order: MarketplaceOrder;
  onClose: () => void;
}

export function OrderDetailSheet({ order, onClose }: OrderDetailSheetProps) {
  const { t, locale } = useLocale();
  const df = dateFnsLocale(locale);
  const category =
    (order.item?.category as MarketplaceCategory) ?? "OTHER";
  const emoji = CATEGORY_EMOJI[category] ?? "📦";

  const userName = order.user
    ? `${order.user.firstName ?? ""} ${order.user.lastName ?? ""}`.trim() ||
      order.user.email ||
      order.userId
    : order.userId;

  const statusStyle = STATUS_STYLES[order.status] ?? STATUS_STYLES.PENDING;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l border-border bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">
            {t("orderDetail.title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
          {/* Status Badge */}
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${statusStyle}`}
            >
              {STATUS_LABEL_KEYS[order.status] ? t(STATUS_LABEL_KEYS[order.status]) : order.status}
            </span>
          </div>

          {/* Item */}
          <div className="rounded-lg border border-border p-4">
            <div className="flex items-center gap-3">
              {order.item?.imageUrl ? (
                <img
                  src={order.item.imageUrl}
                  alt={order.item.name}
                  className="h-12 w-12 rounded-lg border border-border object-cover"
                />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted text-2xl">
                  {emoji}
                </div>
              )}
              <div>
                <p className="font-medium text-foreground">
                  {order.item?.name ?? t("common.unknown")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {CATEGORY_LABEL_KEYS[category] ? t(CATEGORY_LABEL_KEYS[category]) : category}
                </p>
              </div>
            </div>
          </div>

          {/* Details list */}
          <div className="space-y-4">
            <DetailRow
              icon={User}
              label={t("orderDetail.member")}
              value={userName}
              sub={order.user?.email}
            />
            <DetailRow
              icon={Coins}
              label={t("orderDetail.koinsSpent")}
              value={`${order.koinsSpent.toLocaleString()} Koins (≈€${koinsToEur(order.koinsSpent)})`}
            />
            <DetailRow
              icon={Calendar}
              label={t("orderDetail.ordered")}
              value={format(new Date(order.createdAt), "PPp", { locale: df })}
            />
            {order.fulfilledAt && (
              <DetailRow
                icon={Package}
                label={t("orderDetail.fulfilled")}
                value={format(new Date(order.fulfilledAt), "PPp", {
                  locale: df,
                })}
              />
            )}
            {order.cancelledAt && (
              <DetailRow
                icon={Calendar}
                label={t("orderDetail.cancelled")}
                value={format(new Date(order.cancelledAt), "PPp", {
                  locale: df,
                })}
              />
            )}
            {order.fulfillmentDetails && (
              <DetailRow
                icon={FileText}
                label={t("orderDetail.fulfillmentDetails")}
                value={order.fulfillmentDetails}
              />
            )}
            {order.cancellationReason && (
              <DetailRow
                icon={FileText}
                label={t("orderDetail.cancellationReason")}
                value={order.cancellationReason}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="flex gap-3">
      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm text-foreground">{value}</p>
        {sub && (
          <p className="text-xs text-muted-foreground">{sub}</p>
        )}
      </div>
    </div>
  );
}
